import type {
  EexLiveUniverseRollingLadder,
  EexLiveUniverseRollingOverride,
  EexLiveUniverseRollingPolicyResponse,
} from "@chro/shared";

const DELIVERY_KIND_RANK = new Map([
  "day",
  "weekend",
  "week",
  "month",
  "quarter",
  "season",
  "year",
].map((kind, index) => [kind, index] as const));

/**
 * Convert an exact company selection into an equivalent first rolling draft.
 * Empty tenors stay off until an administrator deliberately enables them.
 */
export function seedEexRollingLadders(
  series: ReadonlyArray<EexLiveUniverseRollingPolicyResponse["series"][number]>
): EexLiveUniverseRollingLadder[] {
  return series.flatMap((candidate) => candidate.available
    .filter((availability) => availability.selectedCount > 0)
    .map((availability) => ({
      seriesKey: candidate.seriesKey,
      seriesLabel: candidate.seriesLabel,
      commodityCode: candidate.commodityCode,
      areaCode: candidate.areaCode,
      profile: candidate.profile,
      familyCode: candidate.familyCode,
      currency: candidate.currency,
      uom: candidate.uom,
      deliveryKind: availability.deliveryKind,
      // An exact list records a count, not an intent to absorb every contract
      // the provider may publish later. Preserve that width by default; an
      // administrator can deliberately choose All active in the editor.
      selectionRule: "nearest_n" as const,
      targetCount: availability.selectedCount,
    })))
    .sort(compareLadders);
}

/** Preserve current exact selections that cannot participate in a ladder. */
export function seedEexRollingOverrides(
  memberships: EexLiveUniverseRollingPolicyResponse["memberships"],
  eligibleSubjectIds: ReadonlySet<string>
): EexLiveUniverseRollingOverride[] {
  return memberships
    .filter((membership) => membership.selected
      && membership.seriesKey == null
      && eligibleSubjectIds.has(membership.subjectId))
    .map((membership) => ({ subjectId: membership.subjectId, action: "include" as const }))
    .sort(compareOverrides);
}

export function sameEexRollingDraft(
  leftLadders: readonly EexLiveUniverseRollingLadder[],
  leftOverrides: readonly EexLiveUniverseRollingOverride[],
  rightLadders: readonly EexLiveUniverseRollingLadder[],
  rightOverrides: readonly EexLiveUniverseRollingOverride[]
): boolean {
  return JSON.stringify([...leftLadders].sort(compareLadders))
      === JSON.stringify([...rightLadders].sort(compareLadders))
    && JSON.stringify([...leftOverrides].sort(compareOverrides))
      === JSON.stringify([...rightOverrides].sort(compareOverrides));
}

export function eexRollingLadderKey(
  ladder: Pick<EexLiveUniverseRollingLadder, "seriesKey" | "deliveryKind">
): string {
  return `${ladder.seriesKey}\u0000${ladder.deliveryKind}`;
}

function compareLadders(
  left: EexLiveUniverseRollingLadder,
  right: EexLiveUniverseRollingLadder
): number {
  return left.seriesKey.localeCompare(right.seriesKey)
    || (DELIVERY_KIND_RANK.get(left.deliveryKind) ?? 99)
      - (DELIVERY_KIND_RANK.get(right.deliveryKind) ?? 99);
}

function compareOverrides(
  left: EexLiveUniverseRollingOverride,
  right: EexLiveUniverseRollingOverride
): number {
  return left.subjectId.localeCompare(right.subjectId) || left.action.localeCompare(right.action);
}
